/* © CNES */
import moment from 'moment';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { ProgressBar } from 'primereact/progressbar';
import { Toolbar } from 'primereact/toolbar';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  deleteJob,
  downloadJob,
  getProductUrl,
} from '../../../api/process/process';
import { getProcessingQuicklook } from '../../../api/processings/processings';
import { refProcessesNamesForQuicklook } from '../../../assets/config/filters/Referenciel';
import { ConfigContext } from '../../../context/ConfigContext';
import { Process } from '../../../models/Process';
import { Processing } from '../../../models/Processing';
import { Geotiff } from '../../shared/Geotiff/Geotiff';
import { ExecutionContext } from './ExecutionContext/ExecutionContext';
import './InnerProcessings.scss';

/**
 * InnerProcessingProps interface.
 */
export interface InnerProcessingProps {
  processings: Processing[];
  listProcess: Process[] | null;
  setDoRefetchJobs: Function;
  setDownloadProgress: Function;
}

/**
 * Inner Processings displays a list of jobs for a logged in user in MyData.
 * @param processings The jobs being processed for a user logged in.
 * @param listProcess The selected Processes.
 * @param setDoRefetchJobs Function
 * @param setDownloadProgress Function
 * @returns Element.
 */
export const InnerProcessings = ({
  processings,
  listProcess,
  setDoRefetchJobs,
  setDownloadProgress,
}: InnerProcessingProps) => {
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [tabInfo, setTabInfo] = useState<Processing | undefined>(undefined);
  const [listProcessing, setListProcessing] = useState<Processing[]>([]);
  const context = useContext(ConfigContext);
  const navigate = useNavigate();
  const addTitleInListProcessing = useCallback(
    (processings: Processing[]) => {
      const arrayProcessing: any = [];

      processings?.forEach((element) => {
        let processElement = listProcess?.find(
          (obj: Process) => obj.id === element.processID
        );

        if (processElement) {
          arrayProcessing.push({
            ...element,
            titleProcess: processElement?.title,
          });
        }
      });
      setListProcessing(arrayProcessing);
    },
    [listProcess]
  );
  const [showQuicklookDialog, setShowQuicklookDialog] =
    useState<boolean>(false);
  const [quicklookBlob, setQuicklookBlob] = useState<{
    blob: Blob | undefined;
    processName: string | undefined;
  }>({ blob: undefined, processName: undefined });
  const [quicklookProductName, setQuicklookProductName] = useState<string>();

  /**
   * set ListProcessing on processings change.
   */
  useEffect(() => {
    addTitleInListProcessing(processings);
  }, [processings, addTitleInListProcessing]);

  /**
   * onItemChange setSelectedJobs on click.
   * @param e click event on checkbox.
   */
  const onItemChange = (e: any) => {
    let _selectedJobs = [...selectedJobs];
    if (e.checked) {
      _selectedJobs.push(e.value);
    } else {
      _selectedJobs = _selectedJobs.filter((item) => item !== e.value);
    }
    setSelectedJobs(_selectedJobs);
  };
  /**
   * onItemDetails handle click on details to open dialog.
   * @param event click event.
   * @param data Item data.
   */
  const onItemDetails = (event: any, data: any) => {
    event?.stopPropagation();
    let clickedItem = listProcessing.find((obj) => obj.jobID === data.value);
    setTabInfo(clickedItem);
    setVisible(true);
  };

  const [visible, setVisible] = useState(false);

  /**
   * handle Deselect All Items set SelectedJobs to empty array.
   */
  const handleDeselectAllItems = () => {
    setSelectedJobs([]);
  };

  /**
   * handle Select All Items.
   */
  const handleSelectAllItems = () => {
    let _selectedJobs = [...processings.map((job) => job.jobID)];
    setSelectedJobs(_selectedJobs);
  };

  /**
   * reject delete.
   */
  const reject = () => {};

  /**
   * deleteSelection to delete multiple jobs.
   */
  const deleteSelection = () => {
    confirmDialog({
      message: 'Do you want to delete selected results ?',
      header: 'Delete selected results',
      acceptClassName: 'BtnAccept',
      rejectClassName: 'BtnReject',
      accept: () => {
        const listProcessingToDelete = listProcessing.filter((el) =>
          selectedJobs.includes(el.jobID)
        );
        listProcessingToDelete.forEach((element) => {
          // Throttle deleteJob to API Rate Limit 6 per second.
          setTimeout(() => {
            deleteJob(element.jobID, navigate, context.setError400).then(() => {
              setDoRefetchJobs(Boolean(true));
              // for local dev : we mock the deletion of the job after refetch
              if (process.env.NODE_ENV !== 'production') {
                setListProcessing(listProcessingToDelete);
                let _selectedJobs = selectedJobs.filter((item) =>
                  listProcessingToDelete
                    .map((element) => element.jobID)
                    .includes(item)
                );
                setSelectedJobs(_selectedJobs);
              }
            });
          }, 200);
        });
      },

      reject,
      acceptLabel: 'Yes, Please',
      rejectLabel: 'Nope!',
      className: 'ConfirmDialogRemove',
    });
  };

  /**
   * download job in success.
   * @param e  event
   * @param job job processing
   */
  const downloadJobInSuccess = (e: any, job: Processing) => {
    e.stopPropagation();
    confirmDialog({
      message: 'Do you want to download selected results ?',
      header: 'Download selected results',
      acceptClassName: 'BtnAccept',
      rejectClassName: 'BtnReject',
      accept: () => {
        getProductUrl(job.jobID, navigate, context.setError400).then(
          (result) => {
            context.setDownloadingProducts(job.parameters);
            downloadJob(
              job.parameters,
              result.product_url,
              job.processID,
              context.setError400,
              setDownloadProgress,
              navigate
            ).finally(() => {
              context.setDownloadingProducts(job.parameters);
            });
          }
        );
      },
      reject,
      acceptLabel: 'Yes, Please',
      rejectLabel: 'Nope!',
      className: 'ConfirmDialogRemove',
    });
  };

  /**
   * download all jobs in success.
   */
  const downloadAllSelectedJobs = () => {
    confirmDialog({
      message: 'Do you want to download selected results ?',
      header: 'Download selected results',
      acceptClassName: 'BtnAccept',
      rejectClassName: 'BtnReject',
      accept: () => {
        processings
          .filter((job) => {
            return (
              selectedJobs.includes(job.jobID) && job.status === 'successful'
            );
          })
          .forEach((job, index) => {
            // throttle downloads to 3 requests per second.
            setTimeout(() => {
              getProductUrl(job.jobID, navigate, context.setError400).then(
                (result) => {
                  context.setDownloadingProducts(job.parameters);
                  downloadJob(
                    job.parameters,
                    result.product_url,
                    job.processID,
                    context.setError400,
                    setDownloadProgress,
                    navigate
                  ).finally(() => {
                    context.setDownloadingProducts(job.parameters);
                  });
                }
              );
            }, 334 * index);
          });
      },
      reject,
      acceptLabel: 'Yes, Please',
      rejectLabel: 'Nope!',
      className: 'ConfirmDialogRemove',
    });
  };

  const centerContent = (
    <div className="flex justify-content-center align-items-center">
      <p className="DataReady">{selectedJobs.length} Data selected</p>
      <Button
        icon="pi pi-download"
        label="Download"
        className="p-button-warning p-button-sm button-warning-border ToolbarButton mr-2"
        disabled={!selectedJobs.length}
        onClick={downloadAllSelectedJobs}
      />
      <Button
        label="Stop & Delete"
        className="CustomOutlined ToolbarButton"
        outlined
        disabled={!selectedJobs.length}
        onClick={deleteSelection}
      />
    </div>
  );

  /**
   * confirm Delete
   * @param event click event.
   * @param data data from jobs to delete.
   * @param message
   * @param header
   */
  const confirmDelete = (
    event: any,
    data: any,
    message: string,
    header: string
  ) => {
    event?.stopPropagation();
    confirmDialog({
      message,
      header,
      acceptClassName: 'BtnAccept',
      rejectClassName: 'BtnReject',
      accept: () => {
        deleteJob(data.value, navigate, context.setError400).then(() => {
          setDoRefetchJobs(Boolean(true));
          // for local dev : we mock the deletion of the job after refetch
          if (process.env.NODE_ENV !== 'production') {
            let deleteProcessing = listProcessing.filter(
              (item) => item.jobID !== data.value
            );
            setListProcessing(deleteProcessing);
            let _selectedJobs = selectedJobs.filter((item) =>
              deleteProcessing.map((element) => element.jobID).includes(item)
            );
            setSelectedJobs(_selectedJobs);
          }
        });
      },
      reject,
      acceptLabel: 'Yes, Please',
      rejectLabel: 'Nope!',
      className: 'ConfirmDialogRemove',
    });
  };

  /**
   * format Job Date
   * @param date date as string to format.
   * @returns string date formatted.
   */
  const formatJobDate = (date: string) => {
    return moment(date).format('YYYY-MM-DD HH:mm:ss');
  };

  /**
   * Open Quicklook dialog when tiff image is retrieved from nfs.
   * @param event click event.
   * @param data necessary data to retrieve quicklook from nfs storage.
   */
  const openQuicklook = async (
    event: any,
    data: {
      jobId: string;
      processName: string;
      productId: string;
    }
  ) => {
    event?.stopPropagation();

    const response = await getProcessingQuicklook([
      'processingQuicklook',
      {
        jobId: data.jobId,
        processName: data.processName,
        productId: data.productId,
      },
      navigate,
      context.setError400,
    ]);

    setQuicklookBlob({
      blob: await response.blob(),
      processName: data.processName,
    });
    setShowQuicklookDialog(true);
    setQuicklookProductName(data.productId);
  };

  return (
    <>
      <div className="InnerProcessings">
        <div className="ProcessItemsContainer">
          {!!processings?.length && (
            <div className="SelectButtonsContainer">
              <Button
                label="Select all"
                className="buttonSelectAll"
                onClick={handleSelectAllItems}
                link
              />
              <Button
                label="Deselect all"
                onClick={handleDeselectAllItems}
                link
              />
            </div>
          )}

          <Dialog
            header={
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                  className="iconHeaderContextDialog"
                >
                  <path
                    d="M11 15V11M11 7H11.01M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z"
                    stroke="#002D80"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="titleContextHeaderDialog">
                  Execution context
                </span>
              </div>
            }
            visible={visible}
            style={{ width: '60vw', height: '16vw' }}
            onHide={() => setVisible(false)}
            className="dialogExecution"
          >
            <ExecutionContext contextInfoDetails={tabInfo}></ExecutionContext>
          </Dialog>

          <div className="cardItems">
            {listProcessing?.map((job) => {
              return (
                <Card key={job.jobID}>
                  <div className="flex">
                    <div className="checkbox">
                      <Checkbox
                        aria-label="checkbox"
                        value={job.jobID}
                        onChange={onItemChange}
                        checked={selectedJobs.some(
                          (selectedJobID) => selectedJobID === job.jobID
                        )}
                      />
                    </div>
                    <div
                      className="item"
                      onClick={() =>
                        onItemChange({
                          value: job.jobID,
                          checked: !selectedJobs.some(
                            (selectedJobID) => selectedJobID === job.jobID
                          ),
                        })
                      }
                    >
                      <div className="ItemInfo">
                        <p> {job.titleProcess}</p>
                        <p>{formatJobDate(job.job_start_datetime)}</p>
                      </div>

                      {job.status === 'successful' && (
                        <div className="CompleteJobContainer">
                          <Button
                            label="Success"
                            severity="success"
                            className="statusButton statusSuccess"
                            role="success"
                          />
                        </div>
                      )}
                      {job.status === 'failed' && (
                        <div className="CompleteJobContainer">
                          <Button
                            label="Error"
                            severity="danger"
                            className="statusButton statusError"
                            role="error"
                          />
                        </div>
                      )}
                      {job.status === 'cancelled' && (
                        <div className="CompleteJobContainer">
                          <Button
                            label="Cancelled"
                            severity="danger"
                            className="statusButton statusCancelled"
                          />
                        </div>
                      )}
                      {job.status === 'running' && (
                        <div className="ProgressContainer">
                          <span className="ProgressValue">{job.progress}%</span>
                          <ProgressBar
                            className="Progress"
                            value={job.progress}
                            showValue={false}
                          />
                          <Button
                            label="Cancel"
                            severity="info"
                            className="ButtonCancel"
                            onClick={(event) =>
                              confirmDelete(
                                event,
                                {
                                  value: job.jobID,
                                },
                                'Cancel selected processing ?',
                                'Cancel selected results'
                              )
                            }
                          />
                        </div>
                      )}
                      {job.status === 'dismissed' && (
                        <div className="CompleteJobContainer">
                          <Button
                            label="Canceled"
                            className="statusButton statusCancelled"
                          />
                        </div>
                      )}
                      <div
                        className={`item-icons ${
                          job.status === 'running' && 'item-progress'
                        }`}
                      >
                        <Button
                          rounded
                          text
                          aria-label="infoCircle"
                          icon="pi pi-info-circle"
                          onClick={(event) =>
                            onItemDetails(event, {
                              value: job.jobID,
                              checked: !selectedJobs.some(
                                (selectedJobID) => selectedJobID === job.jobID
                              ),
                            })
                          }
                          style={{ color: 'rgba(255, 255, 255, 1)' }}
                          tooltip="Visualize the execution context"
                          tooltipOptions={{ position: 'top' }}
                        />
                        {job.status === 'successful' && (
                          <Button
                            rounded
                            text
                            role="download"
                            icon="pi pi-download"
                            onClick={(e) => downloadJobInSuccess(e, job)}
                            tooltip="Download"
                            tooltipOptions={{ position: 'top' }}
                            style={{ color: 'rgba(255, 255, 255, 1)' }}
                          />
                        )}
                        {job.status === 'successful' &&
                          refProcessesNamesForQuicklook.includes(
                            job.processID
                          ) && (
                            <Button
                              rounded
                              text
                              aria-label="viewQuicklook"
                              icon="pi pi-eye"
                              onClick={(event) =>
                                openQuicklook(event, {
                                  jobId: job.jobID,
                                  processName: job.processID,
                                  productId: job.parameters,
                                })
                              }
                              tooltip="View"
                              tooltipOptions={{ position: 'top' }}
                              style={{ color: 'rgba(255, 255, 255, 1)' }}
                            />
                          )}
                        {(job.status === 'failed' ||
                          job.status === 'successful') && (
                          <Button
                            rounded
                            text
                            aria-label="trash"
                            icon="pi pi-trash"
                            onClick={(event) =>
                              confirmDelete(
                                event,
                                {
                                  value: job.jobID,
                                },
                                'Do you want to delete selected results ?',
                                'Delete selected results'
                              )
                            }
                            tooltip="Delete"
                            tooltipOptions={{ position: 'top' }}
                            style={{ color: 'rgba(255, 255, 255, 1)' }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          <div className="messageInfo">
            <p>
              Your processings and their results are retained for a period of 1
              month. Beyond that they are automatically deleted.
            </p>
          </div>
        </div>
        <ConfirmDialog />
        <Dialog
          visible={showQuicklookDialog}
          header={quicklookProductName}
          modal={true}
          className="dialogQuicklook"
          onHide={() => setShowQuicklookDialog(false)}
          showHeader={true}
        >
          <div className="GeotiffMapContainer">
            {quicklookBlob && (
              <Geotiff
                blob={quicklookBlob.blob}
                processName={quicklookBlob.processName}
              />
            )}
          </div>
        </Dialog>
      </div>
      <div className="ToolbarContainer">
        <Toolbar center={centerContent} />
      </div>
    </>
  );
};
